/**
 * 全站通用「当面对话」组件（日系 galgame 对话框，C 版：头像在上、正文通栏）。
 *
 * 小助理播报、主线 NPC 台词、经纪人通知共用一套契约：说话人 + 台词 + 可选分支。
 * 版式：纸卡第一行是贴纸头像 + 名字（身份小字次之），其下正文通栏，不再被侧栏挤窄。
 * 分支选项复用随机事件的 .option-btn 语言：检定/标签在前、属性徽章靠左、成功率落右下。
 * 选项可挂 reply 回应句：选定后不收起、原位续播 NPC 反应，读完再收（所有实时对话都走本组件）。
 * **收口只有一个概念**（2026-11-03 茄子令「册页和文字选项是同一种选项的不同呈现」）：一段话的句号要么由玩家
 * 按下某颗选项钮（组件自绘＝`pick`），要么由宿主按下它挂在内容槽里的收口钮（`hold` 交出推进权、`close` 收口）；
 * 按下去之前点屏/点▶/空格一律吞掉。别让宿主在外面自己拦推进——那样这段就成了没人收口的死段，
 * 后面的话全堵在队列里，实测会跨周漏播。带 `stay` 的选项＝点了只回调、不收口，留给最后一颗来结。
 * 播出语义：产生即弹、逐句点继续、读完整块自动收起；没有未读红点，也没有跳过和翻页。
 * 玩家台词（speaker.isPlayer）：不带头像、姓名固定显示「你」——玩家的话不上贴纸（2026-10-12 用户令由「我」改第二人称）。
 */
import { createDiv, createSpan, appendChanges, plotPlain, plotHtml, setPlotText } from './ui';
import { Utils } from '../core/Utils';

/** 打字机每字基础间隔（ms），沿用 E-01② 的实测节奏 */
const TYPESPEED_MS = 34;
/** E-01③：标点后额外停顿，让语句有呼吸 */
const PUNCT_PAUSE: Record<string, number> = {
    '，': 120, '。': 120, '！': 120, '？': 120, '、': 120, '～': 120, '…': 120,
    '；': 70, '：': 70,
};

export interface DialogSpeaker {
    id: string;
    name: string;
    /** 有则用图（捏脸重做完成后在此接线） */
    avatarSrc?: string;
    /** 缺省兜底：姓/名首字色块 */
    avatarChar?: string;
    /** 玩家本人台词：不带头像、姓名固定显示「你」（2026-10-12 用户令，第二人称） */
    isPlayer?: boolean;
    /** 旁白态台词：不带头像、姓名按 name 显示（随机事件=事件标题，2026-10-04 全屏化定） */
    noAvatar?: boolean;
}

export interface DialogLine {
    speaker: DialogSpeaker;
    text: string;
    /** 属性/资源变动：挂在台词行下方（文案打完后浮现，行程文案 .changes 同款），⛔ 不换页 */
    changes?: { label: string; delta: string }[];
    /** 末句可给分支；选项只在整段最后一句上生效 */
    choices?: DialogChoice[];
    /** 回传 choices 的原始下标（与展示顺序无关）；inPlace 选项会附带被点的按钮元素 */
    onPick?: (index: number, btn?: HTMLButtonElement) => void;
}

/** 分支选项：徽章分布照抄月度执行的随机事件，不另立一套 */
export interface DialogChoice {
    text: string;
    /** 属性中文名，有则出「X检定」徽章并按检定型参与排序 */
    attrName?: string;
    /** 成功率 0~100，缺省不出徽章；≥70 薄荷档、<40 暖底档，与随机事件同一判据 */
    prob?: number;
    /** 标签名，有则出标签徽章 */
    tag?: string;
    /** 选中后紧接播出的回应句（不收起、不重弹，读完再走 onEnd）——「选项→NPC 反应」连播用；
     *  数组=多句连播（初遇结识：NPC 反应句+旁白提示行，2026-10-12） */
    reply?: DialogLine | DialogLine[];
    /** 原位演出模式（2026-10-05 D-05 迁移）：选中后卡片与选项行原位保留、全部选项置灰，
     *  由宿主在被点按钮内自行动画（如掷骰），结束后再 play() 结算句续播；期间⛔点卡推进 */
    inPlace?: boolean;
    /** 点了不推进（2026-11-03 统一收口模型）：只回调，本段继续等它的句号。
     *  给「一排选项随便点、只有最后一颗才往下走」那类用；与 reply/inPlace 不并用
     *  （要反应句直接在 onPick 里 play——宿主回调里 play 就是接管本卡）。 */
    stay?: boolean;
}

/** 排队待播的一段：卡上已有段在播时，后来的段排这儿，前一段收口后按序接着播 */
interface VnSegment {
    lines: DialogLine[];
    onEnd: ((picked: number | null) => void) | null;
}

/** 展示顺序：检定/标签在前，纯文本项垫底（与随机事件的 orderOf 同一意图） */
function orderOf(c: DialogChoice): number {
    return c.attrName || c.tag ? 0 : 1;
}

/** D-05（38 批）：3D 骰子每个点数面的「落定朝向」(rotateX, rotateY, 度)——面布局=前1后6/右2左5/顶3底4 */
export const DICE_LAND: Record<number, [number, number]> = {
    1: [0, 0], 2: [0, -90], 3: [-90, 0], 4: [90, 0], 5: [0, 90], 6: [0, -180],
};

/** 点数布局（24×24 viewBox 上的圆心，r=2.3） */
const DICE_PIPS: Record<number, [number, number][]> = {
    1: [[12, 12]],
    2: [[7, 7], [17, 17]],
    3: [[7, 7], [12, 12], [17, 17]],
    4: [[7, 7], [17, 7], [7, 17], [17, 17]],
    5: [[7, 7], [17, 7], [12, 12], [7, 17], [17, 17]],
    6: [[7, 5.5], [7, 12], [7, 18.5], [17, 5.5], [17, 12], [17, 18.5]],
};

/** 3D 骰子立方体（六面点数，静态即朝目标点数；翻滚由调用方写 inline transform 驱动） */
export function createDiceCube(roll: number): HTMLElement {
    const NS = 'http://www.w3.org/2000/svg';
    const cube = document.createElement('span');
    cube.className = `dice-cube land-${roll}`;
    for (let n = 1; n <= 6; n++) {
        const face = document.createElement('span');
        face.className = `dc-face dc-${n}`;
        const svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        for (const [x, y] of DICE_PIPS[n]) {
            const c = document.createElementNS(NS, 'circle');
            c.setAttribute('cx', String(x));
            c.setAttribute('cy', String(y));
            c.setAttribute('r', '2.3');
            c.setAttribute('fill', 'currentColor');
            svg.appendChild(c);
        }
        face.appendChild(svg);
        cube.appendChild(face);
    }
    return cube;
}

export class VnDialog {
    readonly element: HTMLElement;

    private card: HTMLElement;
    private head: HTMLElement;
    private avatarBox: HTMLElement;
    private nameBox: HTMLElement;
    private textBox: HTMLElement;
    private cursor: HTMLElement;
    private choicesBox: HTMLElement;
    /** 属性/资源变动行容器（挂在台词行下方；文案全部打出后才浮现——2026-10-05 用户令） */
    private changesRow: HTMLElement;
    /** ▶ 的常驻底座：有变动行时按钮挪变动行行尾（2026-10-06 用户令「同一行」），无变动行归位此处 */
    private foot: HTMLElement;
    /** 内容槽：宿主把自有内容（月度执行的册行/详情/按钮）挂进卡内——同一张卡的不同内容形态
     *  （2026-10-06 用户令：行程文案框=没有头像姓名的 VN 对话框，对话句/非对话句共用一个组件） */
    readonly contentSlot: HTMLElement;
    private nextBtn: HTMLButtonElement;

    private lines: DialogLine[] = [];
    private li = 0;
    private typing = false;
    private timer: number | null = null;
    private onEnd: ((picked: number | null) => void) | null = null;
    private picked: number | null = null;
    /** 在播段（null＝卡上没东西在说）：排队判据＝这张卡有没有正在播的段 */
    private current: VnSegment | null = null;
    /** 待播队列：前一段收口后按序接着播（后到的排后面，不抢卡也不覆盖前一段的收口回调） */
    private readonly queue: VnSegment[] = [];
    /** 宿主回调进行中（onPick／段尾 onEnd）：此时的自播＝接续本条链，直接接管卡 */
    private hostCallback = 0;
    /** onPick 回调里宿主自行 play 过（如事件结算句续播）⇒ pick 不再 hide 吞掉它 */
    private replayed = false;
    /** inPlace 原位演出期间（宿主在选项行内跑动画）：⛔点卡推进，宿主 play() 时解锁 */
    private inPlaceLock = false;
    /** 本段把推进权交给宿主的内容槽（`hold`）：句号只由宿主的 `close()` 按下。与 `asking` 同一条早退 */
    private held = false;
    /** 常驻模式（月度执行旁白态）：段尾不收卡，advance 重复触发 onEnd（宿主回调幂等——
     *  拦截型宿主重复拦、推进型宿主立即换新段重置回调）；收卡时机归宿主（stop） */
    stayOpen = false;
    /** ▶ 显隐的宿主侧谓词（返回 true=隐藏）：规则单一真相源仍在宿主（如池子页/详情态不出三角），
     *  渲染归组件——finishLine 亮 ▶ 前咨询 */
    nextSuppressed: (() => boolean) | null = null;

    /** 展开/收起时通知宿主（用于让开右下角悬浮入口） */
    onOpenChanged: ((open: boolean) => void) | null = null;

    constructor() {
        this.element = createDiv('vn-dialog');

        this.head = createDiv('vn-head');
        this.avatarBox = createDiv('vn-avatar');
        this.head.appendChild(this.avatarBox);
        this.element.appendChild(this.head);

        this.card = createDiv('vn-card');
        this.element.appendChild(this.card);
        this.nameBox = createSpan('vn-speaker');
        this.card.appendChild(this.nameBox);

        const line = createDiv('vn-line');
        this.textBox = createSpan('vn-text');
        this.cursor = createSpan('vn-cursor');
        line.appendChild(this.textBox);
        line.appendChild(this.cursor);
        this.card.appendChild(line);

        this.changesRow = createDiv('changes');
        this.changesRow.style.display = 'none';
        this.card.appendChild(this.changesRow);

        this.choicesBox = createDiv('vn-choices');
        this.choicesBox.style.display = 'none';
        this.card.appendChild(this.choicesBox);

        // 点 VN 任意处=推进（打字中补满/读完下一句/收起）——推进行为收敛在组件内部，且监听只挂 element 一处：
        // card/head 的重复监听已删（嵌套双监听=一次点击 advance 两跳，第二跳把刚起打的下一句瞬间补满）；
        // stopPropagation 拦住冒泡——宿主面板⛔ 再拿同一次点击 tryAdvance（收起瞬间 show 已移除，
        // 漏拦=同一次点击既收 VN 又推进一周）。2026-10-14 茄子令「对话框剧情都要可以点屏推进，抽成公共组件
        // 统一定义」：element 即全屏命中盒（CSS `.vn-dialog` inset:0，卡片 padding 锚底），点卡/点屏同一监听，
        // 宿主⛔ 再自建「点屏推进」层——月执行页那层只管 VN 收起后的流程推进（点穿态才收得到）。
        this.element.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('button')) return;   // 选项按钮自己消化
            e.stopPropagation();
            this.advance();
        });

        const foot = createDiv('vn-foot');
        this.foot = foot;
        this.nextBtn = document.createElement('button');
        this.nextBtn.type = 'button';
        this.nextBtn.className = 'vn-next';
        this.nextBtn.setAttribute('aria-label', '继续');
        this.nextBtn.textContent = '';
        this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.advance(); });
        foot.appendChild(this.nextBtn);
        this.card.appendChild(foot);

        // 内容槽：卡内最后一位（正文→变动行→选项→▶→宿主内容），宿主挂自有内容用
        this.contentSlot = createDiv('vn-slot');
        this.card.appendChild(this.contentSlot);

        document.addEventListener('keydown', this.onKey);
    }

    get open(): boolean {
        return this.element.classList.contains('show');
    }

    /**
     * 播一段对话（1 条也算一段）；读完/选完回调 onEnd，组件自身收起。
     * 卡上已有段在播就排队，前一段收口后按顺序接着播——后来的不抢卡，也不覆盖前一段的收口回调
     * （2026-11-02 用户令「应该排好按顺序播啊」：组件级排队治「正在进行的通话被抢」）。
     * 两种情况直接接管卡（不排队）：宿主在自己的回调里自播（选项反应句、段尾接下一段），
     * 以及原位演出锁着的时候（掷骰结算句在动画走完之后才来）。
     */
    play(lines: DialogLine[], onEnd?: (picked: number | null) => void): void {
        if (!lines.length) return;
        this.replayed = true;   // 任何 play 都算一次续播信号（pick 据此判断 onPick 是否自播了）
        const seg: VnSegment = { lines, onEnd: onEnd ?? null };
        if (this.hostCallback > 0 || this.inPlaceLock || !this.current) { this.startSegment(seg); return; }
        this.queue.push(seg);
    }

    /**
     * 显式接管当前卡：在播段作废（它的收口不兑现，宿主自己把收口接上），新段立刻上屏；
     * 已排队的段留在后面继续播。宿主明确要换内容时用——例：试镜周选中剧组后把旁白换成到组文案，
     * 那时前一段还占着卡、读不读得毕不由玩家决定，排队会卡住流程。
     */
    playNow(lines: DialogLine[], onEnd?: (picked: number | null) => void): void {
        if (!lines.length) return;
        this.startSegment({ lines, onEnd: onEnd ?? null });
    }

    /** 立即收起且不回调（宿主切换场景时用，例如月度执行弹窗接管屏幕） */
    stop(): void {
        this.stopTimer();
        this.hide();
        this.held = false;
        this.lines = [];
        this.onEnd = null;
        this.current = null;
        if (this.queue.length) {
            // 待播段就此作废：宿主换场景了，这些话没人会再接——出声报，别闷掉
            console.warn(`[VnDialog] stop() 丢弃了 ${this.queue.length} 段待播对话（宿主换场景，待播段作废）`);
            this.queue.length = 0;
        }
    }

    /** 起一段（接管卡）：换内容、清打字机、重渲染说话人 */
    private startSegment(seg: VnSegment): void {
        this.stopTimer();
        this.held = false;      // 接管即作废上一段的收口约定（旧段那颗钮此后按不动流程）
        this.lines = seg.lines;
        this.li = 0;
        this.picked = null;
        this.onEnd = seg.onEnd;
        this.current = seg;
        this.replayed = true;
        this.inPlaceLock = false;   // 新段落开启＝原位演出结束，恢复点卡推进
        this.element.classList.add('show');
        this.onOpenChanged?.(true);
        this.renderSpeaker();
        this.typeLine();
    }

    /** 段收口：兑现这一段的回调（宿主在回调里自播＝接续本链，卡上已有段就别再起新的），再把队里下一段接上 */
    private endSegment(picked: number | null, keepOnEnd: boolean): void {
        const done = this.onEnd;
        if (!keepOnEnd) this.onEnd = null;
        this.current = null;
        this.hostCallback++;
        try { done?.(picked); } finally { this.hostCallback--; }
        this.pumpQueue();
    }

    /** 队里还有段就接着播（前一段收口之后） */
    private pumpQueue(): void {
        if (this.current) return;   // 卡上已有段（刚接上的续播）就继续等
        const next = this.queue.shift();
        if (!next) return;
        this.startSegment(next);
    }

    private hide(): void {
        this.element.classList.remove('show');
        this.onOpenChanged?.(false);
    }

    private renderSpeaker(): void {
        const sp = this.lines[this.li].speaker;
        if (sp.isPlayer) {
            this.avatarBox.style.display = 'none';
            this.nameBox.style.display = '';
            this.nameBox.textContent = '你';
            return;
        }
        if (sp.noAvatar) {
            // 无头像态：只隐藏头像，保留姓名显示（旁白态 name='' 自然不显示；路人角色 name='服装师' 显示姓名）
            this.avatarBox.style.display = 'none';
            this.nameBox.style.display = '';
            this.nameBox.textContent = sp.name;
            return;
        }
        this.avatarBox.style.display = '';
        this.nameBox.style.display = '';
        this.avatarBox.innerHTML = sp.avatarSrc
            ? `<img alt="${sp.name}" src="${sp.avatarSrc}" />`
            : `<span class="vn-avatar-char">${sp.avatarChar || sp.name.slice(0, 1)}</span>`;
        this.nameBox.textContent = sp.name;
    }

    // [[文字]] 高亮的解析口在 `ui.ts`（plotPlain/plotHtml，2026-10-16 提到全站）：打字期用剥了标记的纯文本
    // 逐字打，句末一律换成上色 HTML——三条出口（无障碍瞬出／自然读完／点屏补满）共用下面这只 `completeLine`。
    // 此前点屏那条走的是 `textContent = 原始串`，于是手机上半成品打字被一点就定格成中括号本字（2026-10-16 茄子实测抓到）。
    /** 本句一次打满并上色（唯一出口，别再往 textBox 写带标记的原始串） */
    private completeLine(): void {
        this.stopTimer();
        this.textBox.innerHTML = plotHtml(this.lines[this.li].text);
    }

    private typeLine(): void {
        const full = this.lines[this.li].text;
        this.choicesBox.style.display = 'none';
        this.choicesBox.innerHTML = '';
        this.changesRow.style.display = 'none';   // 换句先撤上一句的变动行
        this.card.classList.remove('asking');
        this.nextBtn.textContent = '';   // 打字期间不出形，读完本句才亮 ▶

        const plain = plotPlain(full);
        if (Utils.prefersReducedMotion()) {
            this.cursor.style.display = 'none';
            this.completeLine();
            this.finishLine();
            return;
        }

        this.cursor.style.display = '';
        this.typing = true;
        this.textBox.textContent = '';
        let i = 0;
        const step = () => {
            i++;
            this.textBox.textContent = plain.slice(0, i);
            if (i >= plain.length) { this.completeLine(); this.finishLine(); return; }
            this.timer = window.setTimeout(step, TYPESPEED_MS + (PUNCT_PAUSE[plain[i - 1]] || 0));
        };
        this.timer = window.setTimeout(step, TYPESPEED_MS);
    }

    private isLastLine(): boolean {
        return this.li >= this.lines.length - 1;
    }

    private finishLine(): void {
        this.stopTimer();
        this.cursor.style.display = 'none';
        const line = this.lines[this.li];
        // 属性/资源变动：文案打完后在句下浮现（rise-in）——同框 ⛔ 换页；▶（vn-foot）同时亮起=信息显示完全
        if (line.changes && line.changes.length > 0) {
            this.changesRow.innerHTML = '';
            appendChanges(this.changesRow, line.changes);   // 与聊天页、行程文案同一份画法
            this.changesRow.appendChild(this.nextBtn);   // ▶ 挂变动行行尾（与行程文案框同形态，2026-10-06 用户令「小三角和变动行在同一行」）
            this.changesRow.style.display = 'flex';
        } else {
            this.foot.appendChild(this.nextBtn);   // 无变动行：▶ 归位底部（appendChild 移动式复用，⛔ 重复 create）
            this.changesRow.style.display = 'none';
        }
        const choices = this.isLastLine() ? line.choices : undefined;
        if (!choices || !choices.length) {
            // ▶ 显隐：本段交出了推进权（hold）就不出三角，否则咨询宿主谓词（详情态等）
            this.nextBtn.style.display = this.held || this.nextSuppressed?.() ? 'none' : '';
            this.nextBtn.textContent = '▶';
            return;
        }
        // 有选项=借用对话框外框，⛔ 出三角（2026-10-05 用户令）
        this.nextBtn.style.display = 'none';
        this.card.classList.add('asking');
        this.choicesBox.style.display = '';
        this.choicesBox.innerHTML = '';
        // 展示顺序与随机事件一致（检定/标签在前），但回调始终回原始下标
        choices.map((c, i) => ({ c, i }))
            .sort((a, b) => orderOf(a.c) - orderOf(b.c))
            .forEach(({ c, i }) => this.choicesBox.appendChild(this.makeChoice(c, i)));
    }

    private makeChoice(c: DialogChoice, index: number): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'option-btn';
        btn.appendChild(setPlotText(createSpan('opt-text'), c.text));
        const meta = createDiv('opt-meta');
        if (c.attrName) meta.appendChild(createSpan('opt-chip attr', `${c.attrName}检定`));
        if (typeof c.prob === 'number') {
            const tone = c.prob >= 70 ? 'hi' : c.prob < 40 ? 'lo' : '';
            meta.appendChild(createSpan(`opt-chip prob${tone ? ' ' + tone : ''}`, `${c.prob}%`));
        }
        if (c.tag) meta.appendChild(createSpan('opt-chip tag', c.tag));
        btn.appendChild(meta);
        btn.addEventListener('click', (e) => { e.stopPropagation(); this.pick(index, btn); });
        return btn;
    }

    private pick(index: number, btn?: HTMLButtonElement): void {
        const line = this.lines[this.li];
        const choice = line.choices?.[index];
        this.picked = index;
        this.replayed = false;
        this.hostCallback++;
        try { line.onPick?.(index, btn); } finally { this.hostCallback--; }
        // 点了不推进的选项：交出推进权等那颗收口键（其余选项照旧可点，句号由无 stay 的那颗或宿主 close 按下）
        if (choice?.stay) { this.hold(); return; }
        // 选项带回应句 → 原位续播（先结算效果再演反应），末句读完才收起走 onEnd
        if (choice?.reply) {
            this.lines = Array.isArray(choice.reply) ? choice.reply : [choice.reply];
            if (this.current) this.current.lines = this.lines;   // 回应句仍属本段（收口不换）
            this.li = 0;
            this.renderSpeaker();
            this.typeLine();
            return;
        }
        if (this.replayed) return;   // onPick 里宿主已 play（事件：执行判定→结算句续播），别 hide 吞掉
        if (choice?.inPlace) {
            // 原位演出模式（D-05 掷骰）：卡片与选项行保留、全部选项置灰、选中行挂 selected，
            // 宿主在被点按钮内跑动画，结束后自行 play() 结算句续播（解锁推进）
            this.inPlaceLock = true;
            this.choicesBox.querySelectorAll('button').forEach(b => { (b as HTMLButtonElement).disabled = true; });
            btn?.classList.add('selected');
            return;
        }
        this.hide();
        this.endSegment(index, false);
    }

    /** 旁白正文被宿主内容顶替（详情页/试镜演出态）时整段让位，恢复由宿主调 false */
    setBodySuppressed(b: boolean): void {
        this.card.classList.toggle('body-suppressed', b);
    }

    /** 打字中先补满本句，再进下一句；末句读完即收起（宿主点屏/点框/点卡共用此推进入口） */
    advance(): void {
        if (!this.open || this.inPlaceLock) return;   // 原位演出期间⛔推进（宿主动画结束后 play 解锁）
        if (this.typing) {
            this.completeLine();   // 补满＝与自然读完同一个出口（一样上色，不写原始串）
            this.finishLine();
            return;
        }
        if (this.held || this.card.classList.contains('asking')) return;   // 等一颗句号钮（选项／宿主收口钮），别吞点击
        if (this.isLastLine()) {
            if (!this.stayOpen) this.hide();
            // 常驻模式保留回调：拦截型宿主（池子页）多次点击重复触发是幂等的，推进型宿主会立即换新段重置回调
            this.endSegment(this.picked, this.stayOpen);
            return;
        }
        this.li++;
        this.renderSpeaker();
        this.typeLine();
    }

    private stopTimer(): void {
        if (this.timer !== null) { window.clearTimeout(this.timer); this.timer = null; }
        this.typing = false;
    }

    private onKey = (e: KeyboardEvent): void => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        if (!this.open) return;
        if (this.card.classList.contains('asking')) return;   // 等玩家用 Tab 选，别抢按键
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        this.advance();
    };

    /**
     * 交出本段的推进权（2026-11-03）：宿主在内容槽里自绘一排选择和那颗收口钮时调本方法——
     * 此后点屏/点▶/空格全被 `advance` 吞掉、▶ 不亮，段只能由 `close()`（或 `playNow()` 换内容）了断。
     * 调用时机＝`play`/`playNow` 之后（本段还没打完字也成立，`finishLine` 会照本判据决定 ▶）。
     */
    hold(): void {
        this.held = true;
        this.nextBtn.style.display = 'none';   // 已亮着的三角当场收回去（hold 常发生在末句已读完之后）
    }

    /** 宿主按下收口钮＝玩家点完最后一颗选项：走与末句点▶完全同一条出口（回调兑现＋队列接着走） */
    close(): void {
        if (!this.current) return;             // 段已被换掉/作废：这颗钮不该再推第二次流程
        this.held = false;
        this.stopTimer();                      // 打字没读完就按收口＝补满后直接收口（同一条出口）
        if (!this.stayOpen) this.hide();
        this.endSegment(this.picked, this.stayOpen);
    }

    /** 从 DOM 摘掉时调用（退出游戏会话） */
    destroy(): void {
        this.stop();
        document.removeEventListener('keydown', this.onKey);
    }
}
